import type {RawAudience} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';
import type {MetricInsightsDropdownItem} from './components/metric-insights/metric-insights.types';

export interface RecipientOpenInsight {
  idEmailMarketingCampaign: string;
  email: string;
  name: string;
}

enum MetricType {
  METRIC_TYPE_CLICK = 'click',
  METRIC_TYPE_OPEN = 'open',
  METRIC_TYPE_DELIVERY = 'delivery',
}

export enum MetricItemType {
  CAMPAIGN = 'c',
  AUDIENCE = 'a',
}

export interface Metrics {
  metricType: MetricType.METRIC_TYPE_DELIVERY | MetricType.METRIC_TYPE_OPEN | MetricType.METRIC_TYPE_CLICK;
  positiveOutcomeLabel: string;
  negativeOutcomeLabel: string;
  positiveOutcomePercentage: number;
  negativeOutcomePercentage: number;
  positiveOutcomeTotal: number;
  negativeOutcomeTotal: number;
  total: number;
}

export interface UnsubscribedNegativeOutcomeDetail {
  type: RecipientInsightsFilter.UNSUBSCRIBED;
  label: string;
  value: number;
  removableValue: number;
}

export interface UnreachableNegativeOutcomeDetail {
  type: RecipientInsightsFilter.UNREACHABLE;
  label: string;
  value: number;
  removableValue: number;
  spamValue: number;
  spamLabel: string;
}

export type NegativeOutcomeDetail = UnsubscribedNegativeOutcomeDetail | UnreachableNegativeOutcomeDetail;

export interface DeliveryMetrics extends Metrics {
  metricType: MetricType.METRIC_TYPE_DELIVERY;
  negativeOutcomeDetails: NegativeOutcomeDetail[];
}

export interface OpenMetrics extends Metrics {
  metricType: MetricType.METRIC_TYPE_OPEN;
}

export interface ClickMetrics extends Metrics {
  metricType: MetricType.METRIC_TYPE_DELIVERY;
}

export interface CampaignClickInsights {
  links: {name: string}[];
}

export interface CampaignMetricDetails {
  idCampaign: string;
  idMailingList: number;
  email: string;
  isDelivered: boolean;
  isBounce: boolean;
  isUnsubscribe: boolean;
  isComplaint: boolean;
  isMarkedAsSpam: boolean;
  isInvalid: boolean;
  isDeleted: boolean;
  numOpens: number;
  numClicks: number;
  clickInsights: CampaignClickInsights;
  openInsights: RecipientOpenInsight[];
}

export interface SingleCampaignMetrics {
  idCampaign: string;
  delivery: DeliveryMetrics;
  open: OpenMetrics;
  click: ClickMetrics;
  details: Record<string, CampaignMetricDetails>;
  score: number;
}

export enum RecipientInsightsSortField {
  EMAIL = 'email',
  DELIVERED = 'delivered',
  OPENS = 'opens',
  CLICKS = 'clicks',
  UNSUBSCRIBED = 'unsubscribed',
}

export enum RecipientInsightsSortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export enum RecipientInsightsFilter {
  UNREACHABLE = 'unreachable',
  UNSUBSCRIBED = 'unsubscribed',
}

export const RECIPIENT_INSIGHTS_DEFAULT_PAGE_LIMIT = 25;
export const FIRST_PAGE_NUMBER = 1;
export const MAX_PAGE_OFFSET = 1000000;
export const REMOVABLE_EMAILS_FETCH_LIMIT = 1000;

export interface EmailMarketingCampaign {
  id: string;
  name: string;
  isDraft: boolean;
  isScheduled: boolean;
  publishOn: string;
  isSharedInTeam: boolean;
  idOwner: number;
}

export interface OptimalTimeResponse {
  maximumUtcTimestamp: number;
  minimumUtcTimestamp: number;
}

export interface MaxOptimalTimePayload {
  idEmailMarketingCampaign: string;
  clientTimezoneOffset: number;
  scheduledDate?: string;
}

export interface UserMailingList {
  id: number;
  name: string;
  createdOn: string;
  numEmails?: number;
  lastViewed?: string;
  numNonDeletedEmails?: number;
  isActive?: boolean;
  validEmailsCount?: number;
  lastUpdatedOn?: string;
  mailingListContacts?: RawAudience[];
  isSmart: boolean;
  isSpam: boolean;
  numNewContacts?: number;
  isSharedInTeam: boolean;
  idOwner: number;
}

export interface MailingListMetricsInsight {
  email: string;
  numClicks: number;
  numOpens: number;
  opens: MetricInsightsDropdownItem[];
  links: MetricInsightsDropdownItem[];
  isUnsubscribe: boolean;
  isBounce: boolean;
  isComplaint: boolean;
  isInvalid: boolean;
  isMarkedAsSpam: boolean;
  isDeleted: boolean;
}

export interface MailingListMetrics {
  idMailingList: number;
  numRecipients: number;
  numDelivered: number;
  numOpens: number;
  numClicks: number;
  insights: Record<string, MailingListMetricsInsight>;
  score: number;
}

export interface CampaignPerformance {
  performanceLoadingState: number;
  selectedItemIds: string[];
  isCollectingData: boolean;
  itemType: MetricItemType.CAMPAIGN | MetricItemType.AUDIENCE;
}


export const DEFAULT_ITEM_ID = 'none';
export const COMPARISON_VIEW_SCREEN_WIDTH_BREAKPOINT_PX = 1000;

export interface RecipientInsightsPaging {
  offset: number;
  limit: number;
  sortField: RecipientInsightsSortField;
  sortDir: RecipientInsightsSortDirection;
}

export interface NegativeOutcomeRecipientCounts {
  numUnreachableRecipients: number;
  numSpamOrComplaintRecipients: number;
  numUnsubscribedRecipients: number;
}

export type FetchNegativeOutcomeCountsArg = {campaignId: string} | {mailingListId: string};

export interface EmailMarketingCampaignPerformanceState {
  performanceLoadingState: number;
  campaigns: Record<string, EmailMarketingCampaign>;
  campaignsWithMetricsAvailable: Record<string, string>;
  campaignsWithMetricsNotAvailable: Record<string, string>;
  campaignsWithMetricsCollecting: Record<string, string>;
  mailingLists: Record<string, UserMailingList>;
  mailingListsWithAvailableMetrics: Record<string, number>;
  mailingListsWithMetricsNotAvailable: Record<string, number>;
  mailingListsWithMetricsCollecting: Record<string, number>;
  campaignMetrics: Record<string, SingleCampaignMetrics>;
  mailingListMetrics: Record<string, MailingListMetrics>;
  selectedItemIds: string[];
  itemType: MetricItemType;
  isNoCampaignCreated: boolean;
  isNoCampaignPublished: boolean;
  isCollectingData: boolean;
  isNoMailingListCreated: boolean;
  isNotCollectingData: boolean;
  detailsLoadingState: number;
  detailsTotalRows: number | null;
  detailsRequestedPaging: RecipientInsightsPaging;
  detailsLoadedRowsPaging: RecipientInsightsPaging;
  detailsLatestRequestId: string;
  latestInitRequestId: string;
  negativeOutcomeCounts: Record<string, NegativeOutcomeRecipientCounts>;
}

export interface FetchCampaignRecipientInsightsArg {
  campaignId: string;
  limit: number;
  offset: number;
  sortField: RecipientInsightsSortField;
  sortDir: RecipientInsightsSortDirection;
}

export interface FetchRemovableCampaignEmailsArg {
  campaignId: string;
  type: RecipientInsightsFilter;
  offset: number;
}

export interface FetchCampaignRecipientInsightsResponse {
  details: Record<string, CampaignMetricDetails>;
  limit: number;
  offset: number;
}

export interface FetchCampaignRecipientInsightsTotalRowsArg {
  campaignId: string;
}

export interface FetchCampaignRecipientInsightsTotalRowsResponse {
  totalRows: number;
}

export interface RemovableEmailsPage {
  emails: string[];
  totalRows: number | null;
}

export interface FetchMailingListRecipientInsightsArg {
  mailingListId: string;
  limit: number;
  offset: number;
  sortField: RecipientInsightsSortField;
  sortDir: RecipientInsightsSortDirection;
}

export interface FetchRemovableMailingListEmailsArg {
  mailingListId: string;
  type: RecipientInsightsFilter;
  offset: number;
}

export interface FetchMailingListRecipientInsightsResponse {
  details: Record<string, MailingListMetricsInsight>;
  limit: number;
  offset: number;
}

export interface FetchMailingListRecipientInsightsTotalRowsArg {
  mailingListId: string;
}

export interface FetchMailingListRecipientInsightsTotalRowsResponse {
  totalRows: number;
}

export type DetailsPageResponsePaging = Pick<FetchCampaignRecipientInsightsResponse, 'limit' | 'offset'>;

export interface InitEmailMarketingCampaignPerformanceArg {
  itemType: string;
  itemIds: string[];
}

export type InitEmailMarketingCampaignPerformanceResponse = Pick<EmailMarketingCampaignPerformanceState, 'itemType' | 'isCollectingData'> &
  Partial<
    Pick<
      EmailMarketingCampaignPerformanceState,
      | 'campaigns' | 'campaignsWithMetricsAvailable' | 'campaignsWithMetricsNotAvailable'
      | 'campaignsWithMetricsCollecting' | 'campaignMetrics' | 'mailingLists'
      | 'mailingListsWithAvailableMetrics' | 'mailingListsWithMetricsNotAvailable'
      | 'mailingListsWithMetricsCollecting' | 'mailingListMetrics'
      | 'isNoCampaignCreated' | 'isNoCampaignPublished' | 'isNoMailingListCreated' | 'isNotCollectingData'
    >
  > & {
    selectedItemIds: (string | number)[];
  };

export interface RemoveEmailsArg {
  id: string;
  type: RecipientInsightsFilter;
  emails: string[];
}

export interface RemoveAllEmailsArg {
  id: string;
  type: RecipientInsightsFilter;
  excludedEmails: string[];
}

export interface PerformanceInputs {
  itemType: string;
  selectedItemIds: string[];
}
